import { useEffect, useState } from 'react';

/** Keyboard is open when the visual viewport is this many px shorter than the layout viewport. */
export const KEYBOARD_OPEN_DELTA = 120;

export function readVisualViewport() {
  if (typeof window === 'undefined') {
    return { height: 667, offsetTop: 0, keyboardOpen: false };
  }
  // Playwright / Jest can pin an iOS keyboard without replacing Chromium's
  // VisualViewport (`window.__CHIPLESS_VV = { height, offsetTop, keyboardOpen }`).
  if (window.__CHIPLESS_VV) {
    const mock = window.__CHIPLESS_VV;
    return {
      height: mock.height,
      offsetTop: mock.offsetTop || 0,
      keyboardOpen: !!mock.keyboardOpen,
    };
  }
  const vv = window.visualViewport;
  const height = vv ? vv.height : window.innerHeight;
  const offsetTop = vv ? vv.offsetTop : 0;
  const keyboardOpen = window.innerHeight - height > KEYBOARD_OPEN_DELTA;
  return { height, offsetTop, keyboardOpen };
}

/**
 * Size a fixed shell (lobby, Host/Join modals) to the visual viewport so iOS
 * Safari's keyboard and toolbar don't cover fields or footer buttons. Pair
 * with `interactive-widget=resizes-content` on the viewport meta (Chrome/Android).
 */
export function useVisualViewport() {
  const [vv, setVv] = useState(readVisualViewport);

  useEffect(() => {
    const update = () => setVv(readVisualViewport());
    update();
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', update);
      viewport.addEventListener('scroll', update);
    }
    window.addEventListener('resize', update);
    return () => {
      if (viewport) {
        viewport.removeEventListener('resize', update);
        viewport.removeEventListener('scroll', update);
      }
      window.removeEventListener('resize', update);
    };
  }, []);

  return vv;
}

export function useOverflowing(ref, deps = []) {
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      setOverflowing(false);
      return undefined;
    }
    const check = () => {
      setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', check);
      el.addEventListener('scroll', check);
      return () => {
        window.removeEventListener('resize', check);
        el.removeEventListener('scroll', check);
      };
    }
    const ro = new ResizeObserver(check);
    ro.observe(el);
    el.addEventListener('scroll', check);
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', check);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return overflowing;
}
